import { useMemo } from 'react';
import {
  HandlersContactResponse,
  useDeleteApiContactsByContactIdMutation,
  useGetApiContactsQuery,
  useGetApiOrganizationsQuery,
  usePatchApiContactsByContactIdMutation,
  usePostApiContactsMutation,
} from '../../../../app/athenaApiGen';
import AdminCatalogPanel, { AdminCatalogSelectOption } from '../AdminCatalogPanel';

// Contact directory (Pattern A). `name` is the only required field; the
// Organization FK is optional, and the three email/phone pairs map to
// gov / work / personal addresses + phones.
//
// PII note: govEmail/workEmail/personalEmail and the two phone numbers
// are PII. Surfacing them in the admin grid is intentional — the whole
// point of the panel is admin lookup/edit. Audit logging is handled
// server-side via the standard admin-action audit trail.
const AdminContactsPanel = () => {
  // Organization FK options. Response is enveloped — pull items[].
  const { data: orgList } = useGetApiOrganizationsQuery({});
  const orgOptions = useMemo<AdminCatalogSelectOption[]>(
    () =>
      (orgList?.items ?? []).map((o) => ({
        value: o.organizationId ?? '',
        label: o.organizationType
          ? `${o.name ?? '(unnamed)'} (${o.organizationType})`
          : (o.name ?? o.organizationId ?? ''),
      })),
    [orgList?.items],
  );

  return (
    <AdminCatalogPanel<HandlersContactResponse>
      tableName="Contact"
      apiPath="/api/contacts"
      pkAccessor="contactId"
      useList={() => useGetApiContactsQuery({})}
      useCreate={usePostApiContactsMutation}
      useUpdate={usePatchApiContactsByContactIdMutation}
      useDelete={useDeleteApiContactsByContactIdMutation}
      buildCreateArg={(v) => ({
        handlersCreateContactRequest: {
          name: (v.name as string) ?? '',
          title: (v.title as string) || undefined,
          address: (v.address as string) || undefined,
          organizationId: (v.organizationId as string) || undefined,
          govEmail: (v.govEmail as string) || undefined,
          workEmail: (v.workEmail as string) || undefined,
          personalEmail: (v.personalEmail as string) || undefined,
          workPhone: (v.workPhone as string) || undefined,
          personalPhone: (v.personalPhone as string) || undefined,
        },
      })}
      buildUpdateArg={(row, v) => ({
        contactId: row.contactId as string,
        handlersUpdateContactRequest: {
          name: (v.name as string) || undefined,
          title: (v.title as string) || undefined,
          address: (v.address as string) || undefined,
          organizationId: (v.organizationId as string) || undefined,
          govEmail: (v.govEmail as string) || undefined,
          workEmail: (v.workEmail as string) || undefined,
          personalEmail: (v.personalEmail as string) || undefined,
          workPhone: (v.workPhone as string) || undefined,
          personalPhone: (v.personalPhone as string) || undefined,
        },
      })}
      buildDeleteArg={(row) => ({ contactId: row.contactId as string })}
      fields={[
        { name: 'name', label: 'Name', type: 'text', required: true },
        { name: 'title', label: 'Title', type: 'text' },
        {
          name: 'organizationId',
          label: 'Organization',
          type: 'select',
          options: orgOptions,
          helperText: 'Optional — the organization this contact belongs to.',
        },
        { name: 'workEmail', label: 'Work Email', type: 'text' },
        { name: 'govEmail', label: 'Gov Email', type: 'text' },
        { name: 'personalEmail', label: 'Personal Email', type: 'text' },
        { name: 'workPhone', label: 'Work Phone', type: 'text' },
        { name: 'personalPhone', label: 'Personal Phone', type: 'text' },
        {
          name: 'address',
          label: 'Address',
          type: 'textarea',
          hideInGrid: true,
        },
      ]}
      fkConflictMessage="Other records (CTPs, TARs, TRs, projects, or program owner refs) still point at this contact. Reassign those references before deleting."
    />
  );
};

export default AdminContactsPanel;
